import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  PaperPlaneTilt,
  Spinner,
  MagnifyingGlass,
  Plus,
  Trash,
  Robot,
  CaretDown,
  CaretRight,
  Folder,
  Paperclip,
  Eye,
  Stop,
  CheckCircle,
  XCircle,
  Warning,
  EyeSlash,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MarkdownMessage from "../components/common/MarkdownMessage";
import { toast } from "sonner";
import {
  sendAssistantMessage,
  getAssistantHistory,
  listSessions,
  updateSession,
  deleteAssistantSession,
  getSubAgents,
  runSubAgent,
  stopSubAgent,
  getPermissions,
  resolvePermission,
} from "../api/assistant";

const STORAGE_KEY = "drawfire_assistant_session_id";

function makeSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getOrCreateSessionId() {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
  } catch (_) {}
  const fresh = makeSessionId();
  try { window.localStorage.setItem(STORAGE_KEY, fresh); } catch (_) {}
  return fresh;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) return "Today";
  if (diff < 172800000) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * ChatPage — Full-page ChatGPT-style chat with history sidebar.
 * Shares the same session system as AssistantChatWidget.
 */
export default function ChatPage() {
  const location = useLocation();
  const [sessionId, setSessionId] = useState(() => getOrCreateSessionId());
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [subAgents, setSubAgents] = useState([]);
  const [showSubAgents, setShowSubAgents] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [attachedFile, setAttachedFile] = useState(null);
  const hasActiveSubAgents = subAgents.some((a) => ["queued", "running"].includes(a.status));

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentSessionRef = useRef(sessionId);
  const deliveredSubAgentIdsRef = useRef(new Set());
  const startedSubAgentIdsRef = useRef(new Set());
  const idCounter = useRef(0);
  const nextId = () => `m-${Date.now()}-${idCounter.current++}`;

  // Load session list
  const loadSessions = useCallback(async () => {
    try {
      const res = await listSessions();
      setSessions(res.data?.sessions || []);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load chat history when session changes
  useEffect(() => {
    if (!sessionId) return;
    currentSessionRef.current = sessionId;
    deliveredSubAgentIdsRef.current = new Set();
    startedSubAgentIdsRef.current = new Set();
    setMessages([]);
    setLoadingHistory(true);
    getAssistantHistory(sessionId)
      .then((res) => {
        const hist = (res.data?.messages || []).map((m) => ({
          id: nextId(),
          role: m.role,
          content: m.content,
          subAgentId: m.sub_agent_id,
        }));
        hist.forEach((m) => {
          if (m.subAgentId) deliveredSubAgentIdsRef.current.add(m.subAgentId);
        });
        setMessages(hist);
      })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [sessionId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const showCompletedSubAgent = useCallback((agent, targetSessionId = sessionId) => {
    if (
      !agent?.id
      || !["completed", "failed"].includes(agent.status)
      || currentSessionRef.current !== targetSessionId
      || deliveredSubAgentIdsRef.current.has(agent.id)
    ) return;
    deliveredSubAgentIdsRef.current.add(agent.id);
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: "assistant",
        content: agent.status === "completed"
          ? `**Background task complete**\n\n${agent.result_summary || "Task completed."}`
          : `**Background task couldn't finish**\n\n${agent.result_summary || "Please try the task again."}`,
        subAgentId: agent.id,
        isError: agent.status === "failed",
      },
    ]);
  }, [sessionId]);

  const launchSubAgent = useCallback((action, targetSessionId = sessionId) => {
    if (!action?.sub_agent_id || startedSubAgentIdsRef.current.has(action.sub_agent_id)) return;
    startedSubAgentIdsRef.current.add(action.sub_agent_id);
    const queued = {
      id: action.sub_agent_id,
      agent_type: action.agent_type,
      task_description: action.task_description,
      status: "queued",
    };
    setSubAgents((prev) => [queued, ...prev.filter((a) => a.id !== queued.id)]);
    runSubAgent(action.sub_agent_id)
      .then((res) => {
        const completed = res.data;
        setSubAgents((prev) => prev.map((a) => a.id === completed.id ? { ...a, ...completed } : a));
        showCompletedSubAgent(completed, targetSessionId);
      })
      .catch(() => {
        // Let the next status poll retry a job whose runner request never
        // reached the server. Server-side status prevents duplicate execution.
        startedSubAgentIdsRef.current.delete(action.sub_agent_id);
      });
  }, [sessionId, showCompletedSubAgent]);

  // Poll sub-agents & permissions while the user continues chatting.
  useEffect(() => {
    if (!sessionId) return;
    let interval;
    const poll = async () => {
      try {
        const [agentsRes, permRes] = await Promise.all([
          getSubAgents(sessionId),
          getPermissions(sessionId),
        ]);
        const agents = agentsRes.data?.sub_agents || [];
        setSubAgents(agents);
        agents.forEach((agent) => showCompletedSubAgent(agent, sessionId));
        agents
          .filter((agent) => agent.status === "queued")
          .forEach((agent) => launchSubAgent({
            sub_agent_id: agent.id,
            agent_type: agent.agent_type,
            task_description: agent.task_description,
          }, sessionId));
        if (!agents.some((a) => ["queued", "running"].includes(a.status))) {
          setShowSubAgents(false);
        }
        setPermissions(permRes.data?.permissions || []);
      } catch {}
    };
    poll();
    if (hasActiveSubAgents) interval = setInterval(poll, 2500);
    return () => clearInterval(interval);
  }, [sessionId, hasActiveSubAgents, showCompletedSubAgent, launchSubAgent]);

  // Start new chat
  const startNewChat = useCallback(() => {
    const fresh = makeSessionId();
    try { window.localStorage.setItem(STORAGE_KEY, fresh); } catch (_) {}
    setSessionId(fresh);
    setMessages([]);
    setSubAgents([]);
    setShowSubAgents(false);
    setAttachedFile(null);
  }, []);

  // Switch to existing session
  const switchSession = useCallback((sid) => {
    try { window.localStorage.setItem(STORAGE_KEY, sid); } catch (_) {}
    setSessionId(sid);
    setSubAgents([]);
    setShowSubAgents(false);
    setAttachedFile(null);
  }, []);

  // Delete session
  const handleDeleteSession = useCallback(async (sid, e) => {
    e.stopPropagation();
    try {
      await deleteAssistantSession(sid);
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (sid === sessionId) startNewChat();
      toast.success("Chat deleted");
    } catch {
      toast.error("Failed to delete chat");
    }
  }, [sessionId, startNewChat]);

  // Send message
  const sendMessage = useCallback(async (text) => {
    if (!text || sending || !sessionId) return;
    setInput("");
    const userMsg = attachedFile
      ? `${text}\n\n📎 Attached: ${attachedFile.name}`
      : text;
    setMessages((prev) => [...prev, { id: nextId(), role: "user", content: userMsg }]);
    setAttachedFile(null);
    setSending(true);
    try {
      const pageContext = {
        route: location.pathname,
        page_type: "chat",
      };
      const res = await sendAssistantMessage(sessionId, text, null, pageContext);
      const { reply, actions } = res.data;
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: "assistant", content: reply, actions: actions || [] },
      ]);
      (actions || [])
        .filter((a) => a.type === "sub_agent_spawned")
        .forEach((a) => launchSubAgent(a, sessionId));
      // Refresh session list (may have new title)
      setTimeout(loadSessions, 2000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const friendly = e.response?.status === 503
        ? "The assistant is temporarily unavailable — please try again shortly."
        : (detail || "Something went wrong — please try again.");
      setMessages((prev) => [...prev, { id: nextId(), role: "assistant", content: friendly, isError: true }]);
    } finally {
      setSending(false);
    }
  }, [sending, sessionId, location.pathname, attachedFile, loadSessions, launchSubAgent]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    sendMessage(text);
  }, [input, sendMessage]);

  // Handle permission resolve
  const handleResolvePermission = useCallback(async (requestId, approved) => {
    try {
      await resolvePermission(requestId, approved);
      setPermissions((prev) => prev.filter((p) => p.id !== requestId));
      toast.success(approved ? "Permission granted" : "Permission denied");
    } catch {
      toast.error("Failed to resolve permission");
    }
  }, []);

  // Handle stop sub-agent
  const handleStopSubAgent = useCallback(async (subAgentId) => {
    try {
      await stopSubAgent(subAgentId);
      setSubAgents((prev) =>
        prev.map((a) => a.id === subAgentId ? { ...a, status: "stopped" } : a)
      );
      toast.success("Sub-agent stopped");
    } catch {
      toast.error("Failed to stop sub-agent");
    }
  }, []);

  // Filter sessions
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase();
    return sessions.filter((s) =>
      (s.title || "Untitled chat").toLowerCase().includes(q)
    );
  }, [sessions, searchQuery]);

  // Group sessions by folder
  const groupedSessions = useMemo(() => {
    const folders = {};
    const root = [];
    filteredSessions.forEach((s) => {
      if (s.folder) {
        if (!folders[s.folder]) folders[s.folder] = [];
        folders[s.folder].push(s);
      } else {
        root.push(s);
      }
    });
    return { folders, root };
  }, [filteredSessions]);

  const runningSubAgents = subAgents.filter((a) => ["queued", "running"].includes(a.status));

  return (
    <div className="flex h-full bg-white" data-testid="chat-page">
      {/* Left Panel — Chat History */}
      <div className="w-[280px] flex-shrink-0 border-r border-slate-200 flex flex-col bg-slate-50">
        {/* New chat button */}
        <div className="p-3 border-b border-slate-200">
          <button
            onClick={startNewChat}
            data-testid="chat-new-button"
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg
                       border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-slate-700"
          >
            <Plus size={16} className="text-slate-500" />
            New Chat
          </button>
        </div>

        {/* Search */}
        <div className="px-3 pt-3 pb-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chats..."
              className="w-full pl-8 pr-3 py-2 text-xs rounded-md border border-slate-200 bg-white
                         placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary/30"
              data-testid="chat-search-input"
            />
          </div>
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {/* Folder groups */}
          {Object.entries(groupedSessions.folders).map(([folder, items]) => (
            <div key={folder} className="mb-1">
              <button
                onClick={() => setExpandedFolders((prev) => ({ ...prev, [folder]: !prev[folder] }))}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold
                           text-slate-500 uppercase tracking-wide hover:text-slate-700"
              >
                {expandedFolders[folder] ? <CaretDown size={10} /> : <CaretRight size={10} />}
                <Folder size={12} />
                <span className="truncate">{folder}</span>
                <span className="ml-auto text-slate-400">{items.length}</span>
              </button>
              {expandedFolders[folder] && items.map((s) => (
                <SessionItem
                  key={s.id}
                  session={s}
                  active={s.id === sessionId}
                  onClick={() => switchSession(s.id)}
                  onDelete={(e) => handleDeleteSession(s.id, e)}
                />
              ))}
            </div>
          ))}

          {/* Root sessions */}
          {groupedSessions.root.map((s) => (
            <SessionItem
              key={s.id}
              session={s}
              active={s.id === sessionId}
              onClick={() => switchSession(s.id)}
              onDelete={(e) => handleDeleteSession(s.id, e)}
            />
          ))}

          {filteredSessions.length === 0 && !loadingHistory && (
            <p className="text-xs text-slate-400 text-center py-6">No conversations yet</p>
          )}
        </div>

        {/* Anonymous toggle */}
        <div className="px-3 py-2.5 border-t border-slate-200 flex items-center gap-2">
          <button
            onClick={() => setAnonymous(!anonymous)}
            className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1.5 rounded-md transition-colors
              ${anonymous ? "bg-violet-50 text-violet-700" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`}
            data-testid="chat-anonymous-toggle"
            title="Anonymous chats won't appear in history"
          >
            {anonymous ? <EyeSlash size={14} /> : <Eye size={14} />}
            {anonymous ? "Anonymous mode: ON" : "Anonymous mode: OFF"}
          </button>
        </div>
      </div>

      {/* Right Panel — Chat Interface */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Robot size={20} className="text-primary" />
            <h1 className="text-sm font-semibold text-slate-800">DrawFire Assistant</h1>
          </div>
          {/* Sub-agent status bar */}
          {runningSubAgents.length > 0 && (
            <button
              onClick={() => setShowSubAgents(!showSubAgents)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full
                         bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
              data-testid="sub-agents-status-bar"
            >
              <Spinner size={12} className="animate-spin" />
              {runningSubAgents.length} background task{runningSubAgents.length > 1 ? "s" : ""} working
              <span className="font-normal text-blue-600">· You can keep chatting</span>
              <span className="text-blue-500 font-semibold">View</span>
            </button>
          )}
        </div>

        {/* Sub-agents dropdown */}
        {showSubAgents && subAgents.length > 0 && (
          <div className="mx-6 mt-2 p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2" data-testid="sub-agents-panel">
            <p className="text-xs font-semibold text-slate-600 mb-2">Sub-Agents</p>
            {subAgents.map((agent) => (
              <div key={agent.id} className="flex items-center justify-between gap-2 text-xs bg-white rounded-md px-3 py-2 border border-slate-100">
                <div className="min-w-0">
                  <span className="font-medium text-slate-700">{agent.agent_type}</span>
                  <span className="ml-2 text-slate-400 truncate">{agent.task_description?.slice(0, 60)}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    ["queued", "running"].includes(agent.status) ? "bg-blue-50 text-blue-700" :
                    agent.status === "completed" ? "bg-green-50 text-green-700" :
                    agent.status === "failed" ? "bg-red-50 text-red-700" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {agent.status}
                  </span>
                  {["queued", "running"].includes(agent.status) && (
                    <button
                      onClick={() => handleStopSubAgent(agent.id)}
                      className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                      title="Stop sub-agent"
                    >
                      <Stop size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loadingHistory && (
            <div className="flex items-center justify-center gap-2 text-slate-400 text-xs py-8">
              <Spinner size={14} className="animate-spin" /> Loading conversation...
            </div>
          )}

          {!loadingHistory && messages.length === 0 && (
            <div className="text-center text-slate-500 text-sm px-4 py-16" data-testid="chat-empty-state">
              <Robot size={36} className="mx-auto mb-3 text-primary/60" />
              <p className="font-medium text-slate-600">Hi, I'm your DrawFire Assistant.</p>
              <p className="text-xs mt-1.5 text-slate-400 max-w-sm mx-auto">
                Ask me to create a new project, find drawings, check financials, or get help with the platform.
                I can also spawn sub-agents for complex tasks.
              </p>
            </div>
          )}

          {/* Permission requests */}
          {permissions.map((perm) => (
            <div key={perm.id} className="flex justify-start" data-testid="permission-request-card">
              <div className="max-w-[80%] px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-2 mb-1.5">
                  <Warning size={14} className="text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800">Permission Request</span>
                </div>
                <p className="text-sm text-amber-900 mb-2">
                  Sub-agent ({perm.agent_type}) wants to: {perm.action_description}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleResolvePermission(perm.id, true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md
                               bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                  >
                    <CheckCircle size={12} /> Allow
                  </button>
                  <button
                    onClick={() => handleResolvePermission(perm.id, false)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md
                               bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
                  >
                    <XCircle size={12} /> Deny
                  </button>
                </div>
              </div>
            </div>
          ))}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-3 ${
                msg.role === "user" ? "message-user" : "message-ai"
              } ${msg.isError ? "!bg-red-50 !text-red-700" : ""}`}>
                {msg.role === "assistant" ? (
                  <>
                    {msg.isError && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <Warning size={13} />
                        <span className="text-xs font-medium">Error</span>
                      </div>
                    )}
                    <MarkdownMessage content={msg.content} />
                    {(msg.actions || []).map((a, i) => (
                      <div key={i}>
                        {a.type === "project_created" && (
                          <div className="mt-2 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                            <CheckCircle size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
                            <div className="text-xs text-emerald-800">
                              <p className="font-semibold">Project created: {a.name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="message-ai px-4 py-3 flex items-center gap-2">
                <Spinner size={14} className="animate-spin text-primary" />
                <span className="text-xs text-slate-500">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 border-t border-slate-200 bg-white px-6 py-4">
          {attachedFile && (
            <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-slate-50 rounded-md border border-slate-200 text-xs text-slate-600">
              <Paperclip size={12} />
              <span className="truncate">{attachedFile.name}</span>
              <button onClick={() => setAttachedFile(null)} className="ml-auto text-slate-400 hover:text-red-500">
                <XCircle size={14} />
              </button>
            </div>
          )}
          <div className="flex gap-2 items-end">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 p-2.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600
                         hover:bg-slate-50 transition-colors"
              title="Attach file"
              data-testid="chat-attach-button"
            >
              <Paperclip size={18} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setAttachedFile(file);
                e.target.value = "";
              }}
            />
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask me anything..."
              disabled={sending}
              className="flex-1 rounded-lg text-sm py-2.5"
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              size="sm"
              className="rounded-lg bg-primary hover:bg-primary-hover flex-shrink-0 px-4 py-2.5"
              data-testid="chat-send-button"
            >
              {sending ? <Spinner size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Individual session item in the history sidebar */
function SessionItem({ session, active, onClick, onDelete }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left group transition-colors mb-0.5 ${
        active
          ? "bg-primary/8 text-primary border border-primary/20"
          : "text-slate-600 hover:bg-white hover:shadow-sm border border-transparent"
      }`}
      data-testid="chat-session-item"
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium truncate">
          {session.title || "Untitled chat"}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {formatDate(session.updated_at || session.created_at)}
        </p>
      </div>
      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500
                   transition-all flex-shrink-0"
        title="Delete chat"
      >
        <Trash size={12} />
      </button>
    </button>
  );
}
